// src/components/AnimatedLogo.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useWindowSize from '../hooks/useWindowSize'; // <-- IMPORT THE HOOK

// Styled-components from before - NO CHANGES NEEDED HERE
const LogoContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 1rem;
  overflow: hidden;
  position: relative;
  z-index: 2;

  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* IE10+/Edge */
  user-select: none; /* Standard */
`;

const AnimationWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  width: min(560px, 90vw);
  margin: 0 auto;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
    width: min(90vw, 420px);
  }
`;

const LogoImage = styled(motion.img)`
  height: 120px;
  width: auto;
  flex-shrink: 0;
  max-width: 100%;
  @media (max-width: 768px) { height: 90px; }
  @media (max-width: 480px) { height: 80px; }
`;

const CompanyNameImage = styled(motion.img)`
  height: 120px;
  width: auto;
  object-fit: contain;
  max-width: 100%;
  @media (max-width: 768px) { height: 90px; }
  @media (max-width: 480px) {
    height: 70px;
    max-width: 100%;
  }
`;


const logoIntrinsic = { width: 968, height: 660 };
const nameIntrinsic = { width: 1757, height: 495 };

const AnimatedLogo = ({ onLogoComplete, readyToAnimate }) => {
  const [showFinalElements, setShowFinalElements] = useState(false);
  // windowWidth is used only to size the second image on desktop

  const [windowWidth] = useWindowSize();
  // Define our breakpoint
  const isDesktop = windowWidth > 768;

  useEffect(() => {
    if (!readyToAnimate) {
      setShowFinalElements(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowFinalElements(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, [readyToAnimate]);

  const logoAnimateProps = readyToAnimate
    ? { opacity: 1, scale: 1 }
    : { opacity: 1, scale: 0.1 };

  const canShowFinalElements = readyToAnimate && showFinalElements;

  // Conditionally create an object with the props needed for desktop
  const desktopProps = isDesktop ? { width: 350, height: 120 } : {};

  const layoutTween = { duration: 0.7, ease: [0.22, 0.61, 0.36, 1] };
  const containerLayoutProps = {
    layout: true,
    transition: { layout: layoutTween },
  };
  const wrapperLayoutProps = {
    layout: true,
    transition: { layout: layoutTween },
  };
  const childLayoutProps = { layout: true };

  return (
    <LogoContainer {...containerLayoutProps}>
      <AnimationWrapper {...wrapperLayoutProps}>
        <LogoImage
          {...childLayoutProps}
          src="/makdis_logo.png"
          alt="MAKDIS Logo"
          width={logoIntrinsic.width}
          height={logoIntrinsic.height}
          initial={{ opacity: 1, scale: 0.1 }}
          animate={logoAnimateProps}
          transition={{
            opacity: { duration: 0.5, ease: 'easeInOut' },
            scale:   { duration: 0.5, ease: 'easeInOut' }
          }}
        />
        <AnimatePresence>
          {canShowFinalElements && (
            <CompanyNameImage
              {...childLayoutProps}
              src="/makdis_name_slogan.png"
              alt="Company Name: MAKDIS"
              width={nameIntrinsic.width}
              height={nameIntrinsic.height}
              // CRITICAL: Spread the conditional props object here
              {...desktopProps} 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut', delay: 0.8 }}
              onAnimationComplete={() => {
                if (onLogoComplete) onLogoComplete();
              }}
            />
          )}
        </AnimatePresence>
      </AnimationWrapper>
    </LogoContainer>
  );
};

export default AnimatedLogo;
